import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Hash, Bot, Zap } from 'lucide-react'
import { useCreateResource, useCheckResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'

interface AddResourceViewProps {
  ownerId: string
}

export default function AddResourceView({ ownerId }: AddResourceViewProps) {
  const navigate = useNavigate()
  const [link, setLink] = useState('')
  const [error, setError] = useState('')
  const [checking, setChecking] = useState(false)
  const [resourceInfo, setResourceInfo] = useState<any>(null)
  const [resourceType, setResourceType] = useState<'channel' | 'bot' | 'boost' | null>(null)
  const [selectedType, setSelectedType] = useState<'channel' | 'bot' | 'boost'>('channel')

  const { isReady, hapticFeedback, hapticNotification, showPopup, showMainButton, hideMainButton, setMainButtonLoading, showBackButton, hideBackButton } = useTelegram()
  const checkResource = useCheckResource()
  const createResource = useCreateResource()

  // Use ref to store the latest handleSubmit without causing re-renders
  const handleSubmitRef = useRef<(() => Promise<void>) | null>(null)

  const handleBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const isBotToken = (input: string) => {
    // Bot token format: 123456789:ABCdefGHI...
    return /^[0-9]+:[A-Za-z0-9_-]{35,}$/.test(input)
  }

  const normalizeLink = (input: string) => {
    // If input starts with @, convert to https://t.me/username
    if (input.startsWith('@')) {
      return `https://t.me/${input.substring(1)}`
    }
    return input
  }

  const handleCheck = useCallback(async () => {
    setError('')
    setResourceInfo(null)
    setResourceType(null)

    if (!link.trim()) {
      setError(selectedType === 'bot' ? 'Введите токен бота' : 'Введите ссылку на канал')
      hapticNotification('error')
      return
    }

    setChecking(true)
    hapticFeedback('medium')

    try {
      const isBotTokenInput = isBotToken(link)
      // Normalize link if it starts with @
      const normalizedLink = normalizeLink(link)

      // For boost, validate as channel but mark as boost type
      if (selectedType === 'boost') {
        const result = await checkResource.mutateAsync({ link: normalizedLink })

        if (!result.exists) {
          setError('Канал не найден. Проверьте ссылку.')
          hapticNotification('error')
          setChecking(false)
          return
        }

        // Show resource info but mark as boost
        setResourceInfo(result)
        setResourceType('boost')
        hapticNotification('success')
      } else {
        // Call backend API with either link or botToken parameter
        const result = await checkResource.mutateAsync(
          isBotTokenInput ? { botToken: link } : { link: normalizedLink }
        )

        if (!result.exists) {
          setError(isBotTokenInput ? 'Бот не найден. Проверьте токен.' : 'Канал не найден. Проверьте ссылку.')
          hapticNotification('error')
          setChecking(false)
          return
        }

        // Show resource info
        setResourceInfo(result)
        setResourceType(isBotTokenInput ? 'bot' : 'channel')
        hapticNotification('success')
      }
    } catch (err: any) {
      console.error('Failed to check resource:', err)
      const errorMessage = err.response?.data?.error || 'Не удалось проверить ресурс'
      setError(errorMessage)
      hapticNotification('error')
    } finally {
      setChecking(false)
    }
  }, [link, checkResource, hapticNotification, selectedType])

  const handleSubmit = useCallback(async () => {
    if (!resourceInfo || !resourceType) {
      setError('Сначала проверьте ресурс')
      hapticNotification('error')
      return
    }

    setMainButtonLoading(true)

    try {
      // Normalize link if it starts with @
      const normalizedLink = normalizeLink(link)

      if (resourceType === 'boost') {
        // Boost resource creation (same as channel but with boost type)
        await createResource.mutateAsync({
          owner_id: ownerId,
          resource_type: 'boost',
          name: resourceInfo.title || normalizedLink,
          username: resourceInfo.username,
          telegram_id: resourceInfo.telegram_id,
          invitation_link: normalizedLink,
          verification_mode: 'verified',
          auto_resource: false,
          filters: {
            men: true,
            women: true,
          },
        })
      } else if (resourceType === 'bot') {
        // Bot resource creation
        const botInvitationLink = resourceInfo.username ? `https://t.me/${resourceInfo.username}` : undefined
        await createResource.mutateAsync({
          owner_id: ownerId,
          resource_type: 'bot',
          username: resourceInfo.username,
          telegram_id: resourceInfo.telegram_id,
          invitation_link: botInvitationLink,
          name: resourceInfo.title || 'Бот',
          verification_bot_token: link,
          verification_mode: 'verified',
          auto_resource: false,
          filters: {
            men: true,
            women: true,
          },
        })
      } else {
        // Channel/Group resource creation
        await createResource.mutateAsync({
          owner_id: ownerId,
          resource_type: 'channel', // Always use 'channel' for now
          name: resourceInfo.title || normalizedLink,
          username: resourceInfo.username,
          telegram_id: resourceInfo.telegram_id,
          invitation_link: normalizedLink,
          verification_mode: 'verified',
          auto_resource: false,
          filters: {
            men: true,
            women: true,
          },
        })
      }

      hapticNotification('success')

      // Navigate back
      navigate('/traffic')

      // Show native Telegram success popup if available
      if (showPopup) {
        try {
          await showPopup({
            title: '✅ Успешно',
            message: 'Ресурс успешно добавлен в систему',
            buttons: [{ type: 'ok' }]
          })
        } catch (e) {
          console.log('Popup not supported, resource added successfully')
        }
      }
    } catch (err: any) {
      console.error('Failed to create resource:', err)
      const errorMessage = err.response?.data?.error || 'Не удалось добавить ресурс'
      setError(errorMessage)
      hapticNotification('error')

      // Show native Telegram error popup
      if (showPopup) {
        try {
          await showPopup({
            title: '❌ Ошибка',
            message: errorMessage,
            buttons: [{ type: 'ok' }]
          })
        } catch (e) {
          console.log('Popup not supported, showing inline error')
        }
      }
    } finally {
      setMainButtonLoading(false)
    }
  }, [resourceInfo, resourceType, link, ownerId, createResource, hapticNotification, showPopup, navigate, setMainButtonLoading])

  // Update ref whenever handleSubmit changes
  useEffect(() => {
    handleSubmitRef.current = handleSubmit
  }, [handleSubmit])

  // Stable wrapper function that always calls the latest handleSubmit
  const stableHandleSubmit = useCallback(async () => {
    if (handleSubmitRef.current) {
      await handleSubmitRef.current()
    }
  }, [])

  // Setup MainButton and BackButton
  useEffect(() => {
    if (!isReady) return

    if (resourceInfo) {
      showMainButton('Добавить ресурс', stableHandleSubmit, '#5288c1', '#ffffff')
    } else {
      hideMainButton()
    }
    showBackButton(handleBack)

    // Hide bottom nav
    document.body.style.setProperty('--hide-bottom-nav', 'true')

    return () => {
      hideMainButton()
      hideBackButton()
      document.body.style.removeProperty('--hide-bottom-nav')
    }
  }, [isReady, resourceInfo, showMainButton, hideMainButton, showBackButton, hideBackButton, stableHandleSubmit, handleBack])

  return (
    <div className="fixed inset-0 bg-tg-bg z-[200] overflow-y-auto pt-safe">
      {/* Content */}
      <div className="flex flex-col items-center px-4 pt-[200px] pb-32">
        {/* Title */}
        <h2 className="text-2xl font-bold text-white mb-3">Добавить ресурс</h2>

        {/* Description */}
        <p className="text-[#8e96a0] text-center text-sm mb-8 max-w-md">
          Выберите тип ресурса и добавьте его в систему
        </p>

        {/* Resource Type Selector */}
        <div className="w-full max-w-md mb-6">
          <div className="flex gap-2 p-1 bg-[#232e3c] rounded-xl">
            <button
              onClick={() => {
                setSelectedType('channel')
                setLink('')
                setResourceInfo(null)
                setResourceType(null)
                setError('')
                hapticFeedback?.('light')
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium transition-all ${
                selectedType === 'channel'
                  ? 'bg-[#5288c1] text-white shadow-md'
                  : 'text-[#8e96a0] hover:text-white'
              }`}
            >
              <Hash className="w-4 h-4" />
              Канал
            </button>
            <button
              onClick={() => {
                setSelectedType('bot')
                setLink('')
                setResourceInfo(null)
                setResourceType(null)
                setError('')
                hapticFeedback?.('light')
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium transition-all ${
                selectedType === 'bot'
                  ? 'bg-[#5288c1] text-white shadow-md'
                  : 'text-[#8e96a0] hover:text-white'
              }`}
            >
              <Bot className="w-4 h-4" />
              Бот
            </button>
            <button
              onClick={() => {
                setSelectedType('boost')
                setLink('')
                setResourceInfo(null)
                setResourceType(null)
                setError('')
                hapticFeedback?.('light')
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium transition-all ${
                selectedType === 'boost'
                  ? 'bg-[#5288c1] text-white shadow-md'
                  : 'text-[#8e96a0] hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              Буст
            </button>
          </div>
        </div>

        {/* Input Field */}
        <div className="w-full max-w-md space-y-4">
          <div>
            <input
              type="text"
              value={link}
              onChange={(e) => {
                setLink(e.target.value)
                setResourceInfo(null)
                setResourceType(null)
                setError('')
              }}
              placeholder={
                selectedType === 'bot'
                  ? '123456:ABCdefGHI...'
                  : 'https://t.me/channel или @username'
              }
              className={`w-full px-4 py-3 bg-[#242f3d] text-white placeholder-[#6d7885] rounded-xl border ${
                error ? 'border-red-500' : 'border-transparent'
              } focus:outline-none focus:border-[#5288c1] transition-colors`}
            />

            {/* Helper text or error */}
            {error ? (
              <p className="mt-2 text-sm text-red-400">{error}</p>
            ) : (
              <p className="mt-2 text-sm text-[#6d7885]">
                {selectedType === 'bot'
                  ? 'Получите токен у @BotFather в Telegram'
                  : selectedType === 'boost'
                  ? 'Вставьте ссылку на канал для буста Telegram'
                  : 'Вставьте ссылку на канал/группу или @username'}
              </p>
            )}
          </div>

          {/* Check Button */}
          {!resourceInfo && (
            <button
              onClick={handleCheck}
              disabled={checking || !link.trim()}
              className={`w-full py-3 rounded-xl font-medium transition-all ${
                checking || !link.trim()
                  ? 'bg-[#2a3a4d] text-[#6d7885] cursor-not-allowed'
                  : 'bg-[#5288c1] text-white hover:bg-[#4a7aaa] active:scale-[0.98]'
              }`}
            >
              {checking ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Проверка...
                </span>
              ) : (
                'Проверить'
              )}
            </button>
          )}

          {/* Resource Info */}
          {resourceInfo && (
            <div className="bg-[#232e3c] rounded-xl p-4 space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#8e96a0]">Тип:</span>
                <span className="text-sm text-white font-medium">
                  {resourceType === 'boost' ? 'Буст' : resourceType === 'bot' ? 'Бот' : 'Канал'}
                </span>
              </div>

              {resourceInfo.title && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#8e96a0]">Название:</span>
                  <span className="text-sm text-white font-medium">{resourceInfo.title}</span>
                </div>
              )}

              {resourceInfo.username && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#8e96a0]">Username:</span>
                  <span className="text-sm text-white font-medium">@{resourceInfo.username}</span>
                </div>
              )}

              {resourceInfo.is_verified_resource && (
                <div className="pt-2 border-t border-[#2a3a4d]">
                  <p className="text-xs text-yellow-400 text-center">
                    ⚠️ Этот ресурс уже зарегистрирован в системе
                  </p>
                </div>
              )}

              {/* Edit button */}
              <button
                onClick={() => {
                  setResourceInfo(null)
                  setResourceType(null)
                  setLink('')
                }}
                className="w-full py-2 text-sm text-[#5288c1] hover:text-[#4a7aaa] transition-colors"
              >
                Изменить ссылку
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
